import React, { useState } from 'react'
import {
  Grid,
  Container,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Alert,
  IconButton,
  Box,
} from '@the-platform-group/component-library'
import { Close as CloseIcon } from '@the-platform-group/component-library/Icons'
import { StepperContainer, useStepper } from '@the-platform-group/component-library/Stepper'
import { PeopleMergeProvider, usePeopleMergeContext } from '../../contexts/PeopleMergeContext'
import SearchInputs from './SearchInputs'
import ComparisonTable from './ComparisonTable'
import MultipleJobsSection from './MultipleJobsSection'
import PreviewMergeSection from './PreviewMergeSection'
import { PEOPLE_MERGE_STEPS } from '../../constants'
import { Button } from '@the-platform-group/component-library/Button'

const MergeFlow = () => {
  const { activeStep, handleNext, handleBack } = useStepper()
  const { mergeData, jobMappingData, reviewMutation, mergeAction } = usePeopleMergeContext()
  const { sourcePerson, targetPerson, sourceId, targetId, preview } = mergeData
  const { jobMappings } = jobMappingData
  const { fetchReview, isLoading: isReviewLoading, clearReview } = reviewMutation
  const { merge, isLoading: isMergeLoading, isError: isMergeError, error: mergeError } = mergeAction

  const [showConfirmDialog, setShowConfirmDialog] = useState(false)

  const hasPreviewData = sourcePerson && targetPerson && preview
  const isLastStep = activeStep === PEOPLE_MERGE_STEPS.length - 1

  const handleNextStep = async () => {
    // When moving from step 1 (Multiple Jobs) to step 2 (Preview & Merge), call review API
    if (activeStep === 1) {
      await fetchReview({
        sourceId,
        targetId,
        jobMappings,
      })
      handleNext()
    } else {
      handleNext()
    }
  }

  const handlePreviousStep = () => {
    // Clear review data when going back from step 2
    if (activeStep === 2) {
      clearReview()
    }
    handleBack()
  }

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <Grid item>
            <ComparisonTable />
          </Grid>
        )
      case 1:
        return (
          <Grid item>
            <MultipleJobsSection />
          </Grid>
        )
      case 2:
        return (
          <Grid item>
            <PreviewMergeSection />
          </Grid>
        )
      default:
        return null
    }
  }

  return (
    <Box sx={{ mt: 3 }}>
      {/* Content Area - Scrollable with max height */}
      <Box
        sx={{
          maxHeight: 'calc(100vh - 280px)', // Viewport - (header ~64px + page header ~120px + footer ~96px)
          overflow: 'auto',
          pb: hasPreviewData ? 0 : 3,
        }}
      >
        <Grid container direction="column" spacing={3}>
          <Grid item container>
            {activeStep !== 2 && <SearchInputs activeStep={activeStep} />}
          </Grid>
          {hasPreviewData && (
            <Grid item>
              <Container maxWidth="sm">
                <StepperContainer steps={PEOPLE_MERGE_STEPS} activeStep={activeStep} />
              </Container>
            </Grid>
          )}
          {renderStepContent()}
        </Grid>
      </Box>

      {/* Footer Wrapper - Shadow on top only */}
      {hasPreviewData && (
        <Box
          sx={{
            boxShadow: '0 -4px 6px -2px rgba(0, 0, 0, 0.1)',
            backgroundColor: 'background.paper',
          }}
        >
          <Box sx={{ pt: 1, px: 3 }}>
            <Grid container justifyContent="space-between" spacing={2}>
              <Grid item>
                {activeStep > 0 && (
                  <Button
                    variant="outlined"
                    color="primary"
                    onClick={handlePreviousStep}
                    disabled={isReviewLoading || isMergeLoading}
                  >
                    Previous
                  </Button>
                )}
              </Grid>
              <Grid item>
                {isLastStep ? (
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => setShowConfirmDialog(true)}
                  >
                    Merge
                  </Button>
                ) : (
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleNextStep}
                    disabled={isReviewLoading}
                    startIcon={
                      isReviewLoading ? <CircularProgress size={16} color="inherit" /> : null
                    }
                  >
                    {isReviewLoading ? 'Loading...' : 'Next'}
                  </Button>
                )}
              </Grid>
            </Grid>
          </Box>
        </Box>
      )}

      <Dialog
        open={showConfirmDialog}
        onClose={() => setShowConfirmDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Grid container justifyContent="space-between" alignItems="center">
            <Grid item>
              <Typography variant="h6">Merge people profile confirmation</Typography>
            </Grid>
            <Grid item>
              <IconButton
                size="small"
                onClick={() => setShowConfirmDialog(false)}
                disabled={isMergeLoading}
              >
                <CloseIcon />
              </IconButton>
            </Grid>
          </Grid>
        </DialogTitle>
        <DialogContent>
          <Grid container direction="column" spacing={2}>
            <Grid item>
              <Alert severity="warning" sx={{ alignItems: 'center' }}>
                This action cannot be undone. Once merged, profiles cannot be reverted.
              </Alert>
            </Grid>
            {isMergeError && mergeError && (
              <Grid item>
                <Alert severity="error" sx={{ alignItems: 'center' }}>
                  {mergeError?.response?.data?.errors?.[0]?.message ||
                    'Failed to merge profiles. Please try again.'}
                </Alert>
              </Grid>
            )}
            <Grid item>
              <Typography variant="body2">
                Are you sure you want to merge these profiles?
              </Typography>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button
            variant="outlined"
            onClick={() => setShowConfirmDialog(false)}
            disabled={isMergeLoading}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() =>
              merge({
                sourceId,
                targetId,
                jobMappings,
              })
            }
            disabled={isMergeLoading}
            startIcon={isMergeLoading ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {isMergeLoading ? 'Merging...' : 'Merge'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

function MergeFlowWithProvider() {
  return (
    <PeopleMergeProvider>
      <MergeFlow />
    </PeopleMergeProvider>
  )
}

export default MergeFlowWithProvider
